<!-- Localized -->
<template>
  <modal-content
    ref="generate-reports-modal"
    name="generate-reports-modal"
    :title="$t('components.filebrowser.generateReport.generate_reports')"
    width="480px"
    :showFooter="false"
    @beforeOpen="onBeforeOpen"
  >
  <template #content>
    <ModalLoader v-if="loading" />
    <div v-else>
        <FormulateForm ref="reportGenerateForm" @submit="handleSubmit" class="flex flex-col gap-4 generate-reports-modal" @validation="onFormValidation">

          <FormulateInput
            v-model="form.selectTemplate"
            validation="required"
            :label="$t('components.filebrowser.generateReport.select_template')"
            :placeholder="$t('components.filebrowser.generateReport.select_template')"
            type="multi-select"
            :multiple="false"
            variant="alt"
            :options="templateOptions"
            :config="{ label: 'template_name', trackBy: 'id' }"
            @input="handleSelectTemplate"
            :element-class="
              (context, classes) => ['flex-1 min-w-full'].concat(classes)
            "
            :disabled="disabledForm"
          />

          <LangMultiselect
              v-model="form.selectedLanguages"
              :options="filteredOptions"
              :validation="[
                  ['required']
              ]"
              :disabled="disabledForm"
              idKey="language_id"
          />

       
          <FormulateInput
            v-model="form.format"
            validation="required"
            :label="$t('components.filebrowser.generateReport.output_file_format')"
            :placeholder="$t('components.filebrowser.generateReport.output_file_format')"
            type="multi-select"
            :multiple="false"
            variant="alt"
            :options="formatOptions"
             @input="handleSelectFormat"
            :element-class="
              (context, classes) => ['flex-1 min-w-full'].concat(classes)
            "
            :config="{ label: 'label', trackBy: 'label' }"
            :disabled="disabledForm"
          />
          <FormulateInput
            ref="reportName"
            v-model="form.reportName"
            validation="required|customValidation"
            :validation-rules="{
              customValidation: customValidationRule
            }"
            :validation-messages="{
              customValidation: $t('components.filebrowser.generateReport.report_name_already_exists')
            }"
            error-behavior="live"
            :label="$t('components.filebrowser.generateReport.report_name')"
            :placeholder="$t('components.filebrowser.generateReport.report_name')"
            type="text"
            :element-class="(context, classes) => ['flex-1 min-w-full', mode === 'view' ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''].concat(classes)"
            :disabled="mode === 'view'"
          />
          <div class="flex justify-end" >
            <button
              v-if="mode !== 'view'"
              type="submit"
              :disabled="formHasErrors || createReportLoader"
             :class="[
                  'text-white font-medium rounded-lg text-sm py-2 flex items-center justify-center',
                  (formHasErrors || createReportLoader) ? 'opacity-50 cursor-not-allowed bg-gray-400' : 'bg-primary hover:bg-primary-focus'
                ]"
                style="height: 40px; width: 160px;"
              >
                <ButtonLoader v-if="createReportLoader" class="h-5 w-5" :size="8"/>
                <span v-else class="w-full text-center">{{ $t('components.filebrowser.generateReport.create_report') }}</span>
              </button>

              <button
              v-else
              type="button"
              class="text-white bg-primary hover:bg-primary-focus font-medium rounded-lg text-sm py-2 px-4 flex items-center justify-center"
              @click="$refs['generate-reports-modal'].hideModal()"
              >
                <span class="w-full text-center">{{ `OK` }}</span>
              </button>
          </div>
        </FormulateForm>
    </div>
  </template>
  </modal-content>
</template>

<script>
import ModalContent from "@shared/modal-content";
import { mapGetters, mapActions } from "vuex";
import LangMultiselect from "@shared/components/LangMultiselect/index.vue";
import ModalLoader from "@shared/components/WrappedLoader";
import ButtonLoader from "@shared/loader";
import axios from "@/axios";

export default {
  name: "GenerateReportsModal",
   components: {
    ModalContent,
    LangMultiselect,
    ModalLoader,
    ButtonLoader
  },
  props: {
    mode: {
      type: String,
      default: "",
    },
    selectedReportInfo: {
      type: Object,
      default: () => ({}),
    },
    existingReportName: {
      type: Array,
      default: () => ([]),
    },
    firstEntity: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      templateOptions: [],
      formatOptions: [],
      loading: false,
      createReportLoader: false,
      form: {
        selectedLanguages: [],
        selectTemplate: null,
        format: null,
        reportName: "",
      },
      formHasErrors: false,
    };
  },
  
  async mounted() {
    await this.fetchUserLangPreference();
    await this.fetchTemplateOptions();
  },
  computed: {
     ...mapGetters({
            currentLangProfile: "LocalizationStore/getCurrentLangProfile",
            langProfileOptions: "LocalizationStore/getUserLangProfileList",
            getSelectedEntity: "getSelectedEntity",
        }),
    filteredOptions(){
      return  [
          ...this.form.selectedLanguages,
          ...this.langProfileOptions.filter(opt => !this.form.selectedLanguages?.find(x=> x.language_id == opt.language_id))
      ]
    },
    disabledForm() {
      return this.mode === "view";
    },
    formatReportName() {
      const date = new Date();
      const day = String(date.getDate()).padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'long' });
      const year = date.getFullYear();
      let lastDotIndex = this.form?.selectTemplate?.template_name?.lastIndexOf('.') ?? -1;
      let nameWithoutExtension = (lastDotIndex > 0) ? this.form.selectTemplate.template_name.substring(0, lastDotIndex) : this.form.selectTemplate.template_name;
      return this.form.selectTemplate ? `${nameWithoutExtension}-${(this.firstEntity?.entity_name || "")}-${day}${month}${year}` : '';
    },
   
  },
 
  methods: {
    ...mapActions({
      fetchUserLangPreference: "LocalizationStore/fetchUserLangPreference",
    }),
    onFormValidation(errors) {
      this.formHasErrors = errors.hasErrors
    },
    customValidationRule({ value }) {
      if (this.mode === "create" && this.existingReportName?.length) {
        const fileType =  this.form?.format?.file_type || '';
        const val = ((value || '') + (fileType ? '.' + fileType : '')).toString().trim().toLowerCase();
        return !this.existingReportName.some(
          v => (v || '').toString().trim().toLowerCase() === val
        );
      }
      return true;
    },
    showModal() {
      if (this.$refs["generate-reports-modal"]) {
        this.$refs["generate-reports-modal"].showModal();
      }
    }, 
    onBeforeOpen() {
      if(this.mode === "create") {
       this.form.selectedLanguages = (this.currentLangProfile && this.currentLangProfile.language_id) ? [this.currentLangProfile] : [];
       this.form.selectTemplate = this.templateOptions?.[0] || null;
       this.handleOptionForFormat(this.form.selectTemplate);
       this.form.reportName = this.formatReportName;
      }
      if(this.mode === "view") {
        this.form.selectedLanguages = [this.selectedReportInfo?.language_profiles] || [];
        this.form.format = this.selectedReportInfo?.output_formats || null;
        this.form.selectTemplate = this.selectedReportInfo?.tenant_template || null;
        this.form.reportName = this.selectedReportInfo?.file_name || "";
      }  
    },
   
    handleSelectTemplate(selectedOption) {
      this.form.reportName = this.formatReportName;
      this.handleOptionForFormat(selectedOption);
    },
    handleSelectFormat() {
      if (this.$refs.reportName && this.$refs.reportName.context) {
        this.$refs.reportName.context.hasBlurred = true;
        this.$refs.reportName.context.performValidation();
      }
    },
    handleOptionForFormat(selectedTemplate) {
      this.formatOptions = selectedTemplate?.["output_formats"] || [];
      this.form.format = this.formatOptions?.[0] || null;
    },
    async handleSubmit() {
      this.createReportLoader = true;
      try {
        const payload = { 
          case_id: this.$route?.query?.caseid,
          file_name: this.form?.reportName,
          file_type: this.form?.format?.file_type,
          template_id: this.form?.selectTemplate.id,
          language_profiles: this.form.selectedLanguages.map(x => x.language_profile_id),
        };
        const url = `report-builder/case/generate`;
        const response =await axios.post(url, payload);
        if(response.status === 200) {
          this.$toast.success(this.$t('components.filebrowser.generateReport.report_submitted_successfully'));
        } 
      } catch(err) {
        console.error("Error in form submission:", err);
        this.$toast.error(err.response?.data?.message || err.response?.data?.details ||  this.$t('general.default_error'));
      } finally {
        this.createReportLoader = false;
        this.$emit("createReports");
        this.$refs["generate-reports-modal"].hideModal();
      }
    },
    async fetchTemplateOptions() {
      try {
        this.loading = true;
        const url = `report-builder/case/${this.$route?.query?.caseid}/templates`;
        const { data }  = await axios.get(url);
        this.templateOptions = data;
        this.loading = false;
      } catch(err) {
        console.error("Error fetching template information:", err);
        this.loading = false;
      }
    }
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .generate-reports-modal {
    .formulate-input {
      margin-bottom: 0 !important;
    }
    .formulate-input-error {
      padding-left: 0px !important;
    }
  }
}
</style>
